/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import {
  CategoryResponse,
  EngineTypeResponse,
  ManufacturerResponse,
  ModelSeriesResponse,
  PowerResponse,
  VehicleResponse,
  VehiclesResponse,
} from '../api/http/vehicleSearch.service';
import { EmailResponse, IbanResponse, PostCodeResponse, PromotionCodeResponse } from '../api/http/validation.service';
import {
  CarInsurerResponse,
  CoveragesResponse,
  InitFlowResponse,
  PaymentOptionsResponse,
  RateResponse,
} from '../api/http/general.service';
import { Recommendation } from '@core/models/swagger/recommendation';
import { Quoting } from '@core/models/swagger/quoting';
import { SummaryLiteData } from '@core/models/swagger/summaryLiteData';

@Injectable({
  providedIn: 'root',
})
export class Mocks {
  /**
   * General Services
   */
  static postPaymentOptions(): Observable<PaymentOptionsResponse> {
    return of({
      frequenciesAndPayments: [
        {
          frequency: 'Yearly',
          kind: 'Debit',
          value: 'jährlich per Lastschrift',
        },
        {
          frequency: 'Yearly',
          kind: 'Transfer',
          value: 'jährlich per Transfer',
        },
      ],
    });
  }

  static postCoverages(): Observable<CoveragesResponse> {
    return of({
      coverages: [
        {
          id: 'Liability',
          value: 'Haftpflicht',
          deductibles: [],
          isRecommended: false,
          defaultDeductible: null,
        },
        {
          id: 'PartiallyComprehensive',
          value: 'Teilkasko',
          deductibles: [
            {
              deductiblePartiallyComprehensive: '150',
              deductibleFullyComprehensive: null,
              value: 'Teilkasko 150€',
            },
            {
              deductiblePartiallyComprehensive: '300',
              deductibleFullyComprehensive: null,
              value: 'Teilkasko 300€',
            },
            {
              deductiblePartiallyComprehensive: '500',
              deductibleFullyComprehensive: null,
              value: 'Teilkasko 500€',
            },
            {
              deductiblePartiallyComprehensive: '1000',
              deductibleFullyComprehensive: null,
              value: 'Teilkasko 1000€',
            },
            {
              deductiblePartiallyComprehensive: '2500',
              deductibleFullyComprehensive: null,
              value: 'Teilkasko 2500€',
            },
          ],
          isRecommended: false,
          defaultDeductible: {
            deductiblePartiallyComprehensive: '150',
            deductibleFullyComprehensive: null,
            value: 'Teilkasko 150€',
          },
        },
        {
          id: 'FullyComprehensive',
          value: 'Vollkasko',
          deductibles: [
            {
              deductibleFullyComprehensive: '150',
              deductiblePartiallyComprehensive: '150',
              value: 'Vollkasko 150€ / Teilkasko 150€',
            },
            {
              deductibleFullyComprehensive: '300',
              deductiblePartiallyComprehensive: '150',
              value: 'Vollkasko 300€ / Teilkasko 150€',
            },
            {
              deductibleFullyComprehensive: '300',
              deductiblePartiallyComprehensive: '300',
              value: 'Vollkasko 300€ / Teilkasko 300€',
            },
            {
              deductibleFullyComprehensive: '500',
              deductiblePartiallyComprehensive: '150',
              value: 'Vollkasko 500€ / Teilkasko 150€',
            },
            {
              deductibleFullyComprehensive: '500',
              deductiblePartiallyComprehensive: '300',
              value: 'Vollkasko 500€ / Teilkasko 300€',
            },
            {
              deductibleFullyComprehensive: '500',
              deductiblePartiallyComprehensive: '500',
              value: 'Vollkasko 500€ / Teilkasko 500€',
            },
            {
              deductibleFullyComprehensive: '1000',
              deductiblePartiallyComprehensive: '150',
              value: 'Vollkasko 1000€ / Teilkasko 150€',
            },
            {
              deductibleFullyComprehensive: '1000',
              deductiblePartiallyComprehensive: '300',
              value: 'Vollkasko 1000€ / Teilkasko 300€',
            },
            {
              deductibleFullyComprehensive: '1000',
              deductiblePartiallyComprehensive: '500',
              value: 'Vollkasko 1000€ / Teilkasko 500€',
            },
            {
              deductibleFullyComprehensive: '1000',
              deductiblePartiallyComprehensive: '1000',
              value: 'Vollkasko 1000€ / Teilkasko 1000€',
            },
            {
              deductibleFullyComprehensive: '2500',
              deductiblePartiallyComprehensive: '2500',
              value: 'Vollkasko 2500€ / Teilkasko 2500€',
            },
          ],
          isRecommended: true,
          defaultDeductible: {
            deductiblePartiallyComprehensive: '150',
            deductibleFullyComprehensive: '500',
            value: 'Vollkasko 500€ / Teilkasko 150€',
          },
        },
      ],
      defaultCoverage: 'FullyComprehensive',
      defaultPaymentOption: {
        kind: 'Debit',
        frequency: 'Yearly',
        value: 'jährlich per Lastschrift',
      },
    });
  }

  static postRate(): Observable<RateResponse> {
    return of({
      productLines: [
        {
          productLine: {
            value: 'Basis',
            id: 'Basic',
          },
          features: [
            {
              name: 'headline_liability',
              list: [
                {
                  name: 'Liability_includes_sign_1',
                  available: true,
                  label: 'Deckungssummen 100 Mio. pauschal, max. 15 Mio. pro Person',
                },
                {
                  name: 'Liability_includes_sign_2',
                  available: false,
                  label: 'Mallorca Police',
                },
                {
                  name: 'Liability_includes_sign_3',
                  available: false,
                  label: 'Eigenschadendeckung bei Sachschäden',
                },
              ],
              label: 'Haftpflicht-Leistungen im Basis-Tarif',
            },
            {
              name: 'headline_comprehensive',
              list: [
                {
                  name: 'comprehensive_includes_sign_1',
                  available: false,
                  label: 'Schäden durch grobe Fahrlässigkeit',
                },
                {
                  name: 'comprehensive_includes_sign_2',
                  available: true,
                  label: 'Schäden durch Tiere aller Art',
                },
                {
                  name: 'comprehensive_includes_sign_3',
                  available: false,
                  label: 'Folgeschäden bei Tierbiss-Schäden bis 5.500 Euro',
                },
              ],
              label: 'Teilkasko-Leistungen im Basis-Tarif',
            },
            {
              name: 'headline_full_comprehensive',
              list: [
                {
                  name: 'full_comprehensive_includes_sign_1',
                  available: false,
                  label: 'Leihwagen nach Unfall bzw. nach Fahrzeugentwendung',
                },
                {
                  name: 'comprehensive_includes_sign_2',
                  label: 'Die vollständige Leistungsübersicht finden Sie auf unseren Informationsseiten',
                },
              ],
              label: 'Vollkasko-Leistungen im Basis-Tarif (Teilkasko ist in Vollkasko enthalten)',
            },
          ],
          info: {
            description: ' Alle wichtigen Absicherungen in der Basis-Absicherung.',
            coverageDescription: 'Kfz-Haftpflicht + Teilkasko + Vollkasko',
            isRecommended: false,
          },
          name: 'cover_details_basis',
          price: {
            default: '7023.20',
            currency: '€',
            frequency: 'jährlich',
          },
          campaign: {
            campaignText: '',
            campaignTeaser: '',
          },
          detailedCoverages: [
            {
              id: 'Liability',
              label: 'Haftpflicht',
              price: '3213.20',
            },
            {
              id: 'FullyComprehensive',
              label: 'Vollkasko',
              price: '3810.00',
            },
          ],
        },
        {
          productLine: {
            value: 'Klassik',
            id: 'Classic',
          },
          features: [
            {
              name: 'headline_liability',
              list: [
                {
                  name: 'Liability_includes_sign_1',
                  available: true,
                  label: 'Deckungssummen 100 Mio. pauschal, max. 15 Mio. pro Person',
                },
                {
                  name: 'Liability_includes_sign_2',
                  available: true,
                  label: 'Mallorca Police',
                },
                {
                  name: 'Liability_includes_sign_3',
                  available: false,
                  label: 'Eigenschadendeckung bei Sachschäden',
                },
              ],
              label: 'Haftpflicht-Leistungen im Klassik-Tarif',
            },
            {
              name: 'headline_comprehensive',
              list: [
                {
                  name: 'comprehensive_includes_sign_1',
                  available: true,
                  label: 'Schäden durch grobe Fahrlässigkeit',
                },
                {
                  name: 'comprehensive_includes_sign_2',
                  available: true,
                  label: 'Schäden durch Tiere aller Art',
                },
                {
                  name: 'comprehensive_includes_sign_3',
                  available: true,
                  label: 'Folgeschäden bei Tierbiss-Schäden bis 5.500 Euro',
                },
              ],
              label: 'Teilkasko-Leistungen im Klassik-Tarif',
            },
            {
              name: 'headline_full_comprehensive',
              list: [
                {
                  name: 'full_comprehensive_includes_sign_1',
                  available: false,
                  label: 'Leihwagen nach Unfall bzw. nach Fahrzeugentwendung',
                },
                {
                  name: 'comprehensive_includes_sign_2',
                  label: 'Die vollständige Leistungsübersicht finden Sie auf unseren Informationsseiten',
                },
              ],
              label: 'Vollkasko-Leistungen im Klassik-Tarif (Teilkasko ist in Vollkasko enthalten)',
            },
          ],
          info: {
            description: ' Der umfassenden Rundum-Schutz zum günstigen Preis',
            coverageDescription: 'Kfz-Haftpflicht + Teilkasko + Vollkasko',
            isRecommended: false,
          },
          name: 'cover_details_classic',
          price: {
            default: '7316.00',
            currency: '€',
            frequency: 'jährlich',
            oldPrice: '8016.40',
            savingAmount: '700.40',
          },
          detailedCoverages: [
            {
              id: 'Liability',
              label: 'Haftpflicht',
              price: '3347.20',
            },
            {
              id: 'FullyComprehensive',
              label: 'Vollkasko',
              price: '3968.80',
            },
          ],
          campaign: {
            campaignText: '',
            campaignTeaser: '',
          },
        },
        {
          productLine: {
            value: 'Premium',
            id: 'Premium',
          },
          features: [
            {
              name: 'headline_liability',
              list: [
                {
                  name: 'Liability_includes_sign_1',
                  available: true,
                  label: 'Deckungssummen 100 Mio. pauschal, max. 15 Mio. pro Person',
                },
                {
                  name: 'Liability_includes_sign_2',
                  available: true,
                  label: 'Mallorca Police',
                },
                {
                  name: 'Liability_includes_sign_3',
                  available: true,
                  label: 'Eigenschadendeckung bei Sachschäden',
                },
              ],
              label: 'Haftpflicht-Leistungen im Premium-Tarif',
            },
            {
              name: 'headline_comprehensive',
              list: [
                {
                  name: 'comprehensive_includes_sign_1',
                  available: true,
                  label: 'Schäden durch grobe Fahrlässigkeit',
                },
                {
                  name: 'comprehensive_includes_sign_2',
                  available: true,
                  label: 'Schäden durch Tiere aller Art',
                },
                {
                  name: 'comprehensive_includes_sign_3',
                  available: true,
                  label: 'Folgeschäden bei Tierbiss-Schäden bis 5.500 Euro',
                },
              ],
              label: 'Teilkasko-Leistungen im Premium-Tarif',
            },
            {
              name: 'headline_full_comprehensive',
              list: [
                {
                  name: 'full_comprehensive_includes_sign_1',
                  available: true,
                  label: 'Leihwagen nach Unfall bzw. nach Fahrzeugentwendung',
                },
                {
                  name: 'comprehensive_includes_sign_2',
                  label: 'Die vollständige Leistungsübersicht finden Sie auf unseren Informationsseiten',
                },
              ],
              label: 'Vollkasko-Leistungen im Premium-Tarif (Teilkasko ist in Vollkasko enthalten)',
            },
          ],
          info: {
            description: ' Optimale Absicherung mit Premium-Schutz',
            coverageDescription: 'Kfz-Haftpflicht + Teilkasko + Vollkasko',
            isRecommended: true,
          },
          name: 'cover_details_premium',
          price: {
            default: '8413.20',
            currency: '€',
            frequency: 'jährlich',
            oldPrice: '9218.80',
            savingAmount: '805.60',
          },
          detailedCoverages: [
            {
              id: 'Liability',
              label: 'Haftpflicht',
              price: '3849.20',
            },
            {
              id: 'FullyComprehensive',
              label: 'Vollkasko',
              price: '4564.00',
            },
          ],
          campaign: {
            campaignText: '',
            campaignTeaser: '',
          },
        },
      ],
      internalNcdClasses: {
        ncdClassLiability: 'SF04',
        ncdClassFullyComprehensive: 'SF05',
      },
      tariffSet: 'Normal',
    });
  }

  static postRecommendation(): Observable<Recommendation> {
    return of({
      recommendations: {
        tariffOptionsBasic: [
          {
            id: 'SCH',
            paymentPeriod: 'Yearly',
            priceDefault: '18.40',
            isSelected: false,
          },
          {
            id: 'FAS',
            paymentPeriod: 'Yearly',
            priceDefault: '26.00',
            isSelected: false,
          },
        ],
        kaskoClever: {
          id: 'KaskoClever',
          paymentPeriod: 'Yearly',
          discount: {
            priceWithDiscount: '7023.20',
            percentage: 'bis zu 15',
            priceBefore: '7695.60',
          },
          isSelected: false,
        },
      },
      detailedCoverages: [
        {
          id: 'Liability',
          label: 'Haftpflicht',
          price: '3213.20',
        },
        {
          id: 'FullyComprehensive',
          label: 'Vollkasko',
          price: '4482.40',
        },
      ],
      finalPrices: [
        {
          id: 'Basic',
          price: {
            default: '7695.60',
            currency: '€',
            frequency: 'jährlich',
          },
          cover: {
            productLine: 'Basic',
            coverages: [
              {
                coverageType: 'Liability',
                price: '3213.20',
              },
              {
                coverageType: 'FullyComprehensive',
                price: '4482.40',
              },
            ],
            optionalCoverages: [
              {
                tariffOption: 'SCH',
                price: '18.40',
              },
              {
                tariffOption: 'FAS',
                price: '26.00',
              },
              {
                tariffOption: 'KaskoClever',
                price: '-672.40',
              },
            ],
            annualPrice: '7695.60',
          },
        },
        {
          id: 'Classic',
          price: {
            default: '8016.40',
            currency: '€',
            frequency: 'jährlich',
          },
          cover: {
            productLine: 'Classic',
            coverages: [
              {
                coverageType: 'Liability',
                price: '3347.20',
              },
              {
                coverageType: 'FullyComprehensive',
                price: '4669.20',
              },
            ],
            optionalCoverages: [
              {
                tariffOption: 'SCH',
                price: '18.40',
              },
              {
                tariffOption: 'FAS',
                price: '26.00',
              },
              {
                tariffOption: 'KaskoClever',
                price: '-700.40',
              },
            ],
            annualPrice: '8016.40',
          },
        },
        {
          id: 'Premium',
          price: {
            default: '9218.80',
            currency: '€',
            frequency: 'jährlich',
          },
          cover: {
            productLine: 'Premium',
            coverages: [
              {
                coverageType: 'Liability',
                price: '3849.20',
              },
              {
                coverageType: 'FullyComprehensive',
                price: '5369.60',
              },
            ],
            optionalCoverages: [
              {
                tariffOption: 'SCH',
                price: '18.40',
              },
              {
                tariffOption: 'FAS',
                price: '26.00',
              },
              {
                tariffOption: 'KaskoClever',
                price: '-805.60',
              },
            ],
            annualPrice: '9218.80',
          },
        },
      ],
      currency: '€',
      internalNcdClasses: {
        ncdClassLiability: 'SF04',
        ncdClassFullyComprehensive: 'SF05',
      },
      price: {
        default: '7695.60',
        currency: '€',
        frequency: 'jährlich',
      },
      cover: {
        productLine: 'Basic',
        coverages: [
          {
            coverageType: 'Liability',
            price: '3213.20',
          },
          {
            coverageType: 'FullyComprehensive',
            price: '4482.40',
          },
        ],
        optionalCoverages: [
          {
            tariffOption: 'SCH',
            price: '18.40',
          },
          {
            tariffOption: 'FAS',
            price: '26.00',
          },
          {
            tariffOption: 'KaskoClever',
            price: '-672.40',
          },
        ],
        annualPrice: '7695.60',
      },
    });
  }

  static getCarInsurers(): Observable<CarInsurerResponse> {
    return of({
      carInsurers: [
        {
          value: 'ADAC Autoversicherung AG',
          id: '5135',
        },
        {
          value: 'ADLER Versicherung',
          id: '5581',
        },
        {
          value: 'AdmiralDirekt.de',
          id: '1113',
        },
        {
          value: 'Aioi Motor and General Insurance',
          id: '5029',
        },
        {
          value: 'Allianz AG',
          id: '5312',
        },
        {
          value: 'Allianz Direct Versicherungs-AG',
          id: '1117',
        },
        {
          value: 'AllSecur Deutschland AG',
          id: '1116',
        },
        {
          value: 'Alpha Insurance A/S',
          id: '7778',
        },
        {
          value: 'Alte Leipziger',
          id: '5405',
        },
        {
          value: 'Amazon - Volvo Versicherungsdienst',
          id: '5318 1',
        },
        {
          value: 'Ammerländer Versicherung VVaG',
          id: '5068',
        },
        {
          value: 'ARAG Allgemeine',
          id: '5455',
        },
        {
          value: 'Asstel Sachversicherung AG',
          id: '5858#1',
        },
        {
          value: 'Avetas Versicherungs-AG',
          id: '5132',
        },
        {
          value: 'AXA Colonia / Sicher Direct / Albingia Versicherung',
          id: '5515#1',
        },
        {
          value: 'AXA easy Versicherung AG',
          id: '5155',
        },
        {
          value: 'AXA Versicherung AG',
          id: '5515',
        },
        {
          value: 'BA die Bayerische Allgemeine Versicherung AG',
          id: '5310',
        },
        {
          value: 'Badische Allgemeine',
          id: '5593',
        },
        {
          value: 'Badische Gemeinde-Versicherungs-Verband',
          id: '5316',
        },
        {
          value: 'Baloise Sachversicherungs-AG',
          id: '5633',
        },
        {
          value: 'Barmenia Allgemeine',
          id: '5317',
        },
        {
          value: 'Bavaria Direkt Versicherung AG',
          id: '5787',
        },
        {
          value: 'Bayerische Versicherungsbank',
          id: '5325',
        },
        {
          value: 'Bayerischer Versicherungsverband VAG',
          id: '5324',
        },
        {
          value: 'BGV-Versicherung Aktiengesellschaft',
          id: '5146',
        },
        {
          value: 'Chartis Europe S.A.',
          id: '5595',
        },
        {
          value: 'Chubb European Group Limited',
          id: '5902',
        },
        {
          value: 'Concordia Versicherung',
          id: '5338',
        },
        {
          value: 'Condor Allgemeine',
          id: '5339',
        },
        {
          value: 'Continentale Sachversicherung',
          id: '5340',
        },
        {
          value: 'Cordial Versicherung AG',
          id: '5049',
        },
        {
          value: 'Cosmos',
          id: '5552',
        },
        {
          value: 'DA Deutsche Allgemeine',
          id: '5343',
        },
        {
          value: 'DaimlerChrysler / debis',
          id: '5364 013',
        },
        {
          value: 'DARAG Deutschland AG',
          id: '5771',
        },
        {
          value: 'DBV - Winterthur',
          id: '5515#3',
        },
        {
          value: 'DBV Beamten-Versicherung',
          id: '5311',
        },
        {
          value: 'DBV-WinSelect',
          id: '5515#2',
        },
        {
          value: 'Debeka',
          id: '5549',
        },
        {
          value: 'Deutsche Internet Versicherung AG',
          id: '5084',
        },
        {
          value: 'DEVK Allgemeine Versicherung AG',
          id: '5513',
        },
        {
          value: 'DEVK Sach Versicherung AG',
          id: '5344',
        },
        {
          value: 'Dialog Versicherung AG',
          id: '5210',
        },
        {
          value: 'Die Alternative Versicherung AG',
          id: '5052',
        },
        {
          value: 'EMIL Deutschland',
          id: '1115',
        },
        {
          value: 'ERGO Direkt Versicherung AG',
          id: '5562',
        },
        {
          value: 'ERGO Mobility Solutions',
          id: '1120',
        },
        {
          value: 'EUROPA Versicherung AG',
          id: '5508',
        },
        {
          value: 'Fahrlehrerversicherung',
          id: '5470',
        },
        {
          value: 'Feuersozietät Berlin Brandenburg',
          id: '5024',
        },
        {
          value: 'Fiat',
          id: '5364 010',
        },
        {
          value: 'Freeyou Insurance AG',
          id: '5033',
        },
        {
          value: 'FRIDAY',
          id: '5209',
        },
        {
          value: 'Garanta / Ford',
          id: '5505',
        },
        {
          value: 'Gegenseitigkeit Versicherung Oldenburg',
          id: '5365',
        },
        {
          value: 'Generali Deutschland Versicherung AG',
          id: '5342',
        },
        {
          value: 'Gerling-Konzern Allgemeine',
          id: '5368',
        },
        {
          value: 'GHV Gemeinnützige Haftpflicht-Versicherungsanstalt',
          id: '0523',
        },
        {
          value: 'Gothaer Allgemeine Versicherung AG',
          id: '5858',
        },
        {
          value: 'GVO Gegenseitigkeit Versicherung Oldenburg VVaG',
          id: '5365',
        },
        {
          value: 'GVV Direktversicherung AG',
          id: '5585',
        },
        {
          value: 'Hamburger Feuerkasse Versicherungs-AG',
          id: '5032',
        },
        {
          value: 'Hannoversche Direkt',
          id: '5862#1',
        },
        {
          value: 'HanseMerkur Allgemeine Versicherung AG',
          id: '5501',
        },
        {
          value: 'HDI Global SE',
          id: '5096',
        },
        {
          value: 'HDI Versicherung AG',
          id: '5085',
        },
        {
          value: 'HDI-Gerling Firmen und Privat Versicherung AG',
          id: '5512',
        },
        {
          value: 'Helvetia',
          id: '5384',
        },
        {
          value: 'Helvetia Versicherungs-Aktiengesellschaft',
          id: '5448',
        },
        {
          value: 'Honda',
          id: '5364 014',
        },
        {
          value: 'HUK 24',
          id: '5086',
        },
        {
          value: 'HUK-COBURG Beamten',
          id: '5375',
        },
        {
          value: 'HUK-COBURG-Allgemeine',
          id: '5521',
        },
        {
          value: 'InShared',
          id: '7970',
        },
        {
          value: 'International Insurance Corporation - ehem. Ineas Insurance',
          id: '7587',
        },
        {
          value: 'iptiQ EMEA P&C S.A.',
          id: '5225',
        },
        {
          value: 'Itzehoer',
          id: '5401',
        },
        {
          value: 'Janitos Versicherung AG',
          id: '5078',
        },
        {
          value: 'Karlsruher Beamten',
          id: '5570',
        },
        {
          value: 'Karlsruher Versicherung',
          id: '5509',
        },
        {
          value: 'KRAFT',
          id: '5550',
        },
        {
          value: 'KRAVAG-Allgemeine',
          id: '5058',
        },
        {
          value: 'KRAVAG-LOGISTIC Versicherungs-AG',
          id: '5080',
        },
        {
          value: 'Landesschadenhilfe Versicherungs-AG',
          id: '5362',
        },
        {
          value: 'Lippische Landesbrandversicherung AG',
          id: '5232',
        },
        {
          value: 'LVM',
          id: '5402',
        },
        {
          value: 'Mannheimer Versicherung AG',
          id: '5061',
        },
        {
          value: 'MAPFRE RE',
          id: '6801',
        },
        {
          value: 'Mecklenburgische Versicherungs-Gesellschaft A.G.',
          id: '5412',
        },
        {
          value: 'Münchener Verein Versicherungsgruppe',
          id: '5414',
        },
        {
          value: 'Neodigital Autoversicherung AG',
          id: '5241',
        },
        {
          value: 'nexible Versicherung AG',
          id: '5070',
        },
        {
          value: 'Niederlassung der FRIDAY Insurance S.A.',
          id: '5209',
        },
        {
          value: 'Nippon Insurance Company Of Europe Ltd',
          id: '5680',
        },
        {
          value: 'Nissan',
          id: '5364 012',
        },
        {
          value: 'NOVA',
          id: '5390',
        },
        {
          value: 'NÜRNBERGER Allgemeine',
          id: '5426',
        },
        {
          value: 'NÜRNBERGER Beamten',
          id: '5686',
        },
        {
          value: 'Öffentliche Feuerversicherung Sachsen-Anhalt',
          id: '0522',
        },
        {
          value: 'Öffentliche Versicherung Braunschweig',
          id: '0501',
        },
        {
          value: 'Oldenburgische Landesbrandkasse',
          id: '0506',
        },
        {
          value: 'ONTOS',
          id: '5791',
        },
        {
          value: 'Opel-Händler Versicherungsdienst GmbH',
          id: '5364 009',
        },
        {
          value: 'OPTIMA',
          id: '5519',
        },
        {
          value: 'Patria Versicherung',
          id: '5432',
        },
        {
          value: 'Provinzial Brandkasse / Prov. Nord Brandkasse',
          id: '5446',
        },
        {
          value: 'Provinzial Versicherung AG Standort Düsseldorf',
          id: '1118',
        },
        {
          value: 'Provinzial Versicherung AG Standort Münster',
          id: '1119',
        },
        {
          value: 'R & V Allgemeine',
          id: '5438',
        },
        {
          value: 'R+V Direktversicherung',
          id: '5137',
        },
        {
          value: 'Renault / RVD',
          id: '5364 011',
        },
        {
          value: 'Rheinland Versicherung AG',
          id: '5798',
        },
        {
          value: 'Rhion Versicherung AG',
          id: '5121',
        },
        {
          value: 'SAARLAND',
          id: '5773',
        },
        {
          value: 'Schwarzmeer und Ostsee / SOVAG',
          id: '5690',
        },
        {
          value: 'Securitas',
          id: '5450',
        },
        {
          value: 'SIGNAL IDUNA Allgemeine Versicherung AG',
          id: '5125',
        },
        {
          value: 'Sparkassen DirektVersicherung AG',
          id: '5051',
        },
        {
          value: 'Sparkassen-Versicherung Sachsen',
          id: '5781',
        },
        {
          value: 'SV SparkassenVersicherung Gebäudeversicherung',
          id: '5036',
        },
        {
          value: 'uniVersa',
          id: '5463',
        },
        {
          value: 'VAV / ARTUS',
          id: '7258',
        },
        {
          value: 'Versicherer im Raum der Kirchen',
          id: '5098',
        },
        {
          value: 'Versicherungskammer Bayern',
          id: '5042',
        },
        {
          value: 'Versicherungsverband für Gemeinden',
          id: '5469',
        },
        {
          value: 'Verti (ehem. Direct Line)',
          id: '5055',
        },
        {
          value: 'VGH - Landschaftliche Brandkasse Hannover',
          id: '5400',
        },
        {
          value: 'VHV Versicherung',
          id: '5862',
        },
        {
          value: 'Volkswagen Autoversicherung AG',
          id: '5169',
        },
        {
          value: 'VOLKSWOHL-BUND',
          id: '5484',
        },
        {
          value: 'VVD - Volkswagen Versicherungsdienst',
          id: '1112',
        },
        {
          value: 'wefox Insurance AG',
          id: '9496',
        },
        {
          value: 'WGV',
          id: '5525',
        },
        {
          value: 'Württembergische Gemeinde-Versicherung AG',
          id: '5479',
        },
        {
          value: 'Württembergische Versicherung',
          id: '5783',
        },
        {
          value: 'WWK Allgemeine Versicherung AG',
          id: '5476',
        },
        {
          value: 'Zurich Connect',
          id: '7929',
        },
        {
          value: 'Zurich Insurance plc NFD',
          id: '5151',
        },
      ],
    });
  }

  static postInitFlow(): Observable<InitFlowResponse> {
    return of({
      campaignInfo: {
        telephoneNumber: '030 - 890 003 004',
        initialImage: '',
        specialTariffImage: '',
        campaignCode: 'defaultCampaignCode',
        marketingId: '7583i011',
        cooperationId: '00',
      },
      promotionInfo: {},
      correlationId: 'ffd3591d-daee-40a1-8ce0-f504c8ba9095',
      ratingDate: '2025-03-07',
      parameters: [
        {
          name: 'CO2Info',
          value: {
            offer: 'TEST CO2-Rabatt: 10% bereits abgezogen',
            speechBubble: {
              title: ' Dieses Fahrzeug erhält einen  CO2-Rabatt!',
              subtitle: '10% ERSPARNIS auf den Gesamtpreis.',
            },
            modal: {
              title: 'Herzlichen Glückwunsch',
              description:
                'Mit Ihrem Auto sind Sie umweltfreundlich unterwegs. Dafür belohnen wir Sie mit unserem exklusiven CO2-Rabatt!',
              bullets: [
                '10% ERSPARNIS auf den Gesamtpreis 10%*',
                'Bei Buchung des Schutzbriefs: Abschleppservice, wenn der Akku mal leer ist',
                'Absicherung des Ladekabels im Falle eines Diebstahls',
              ],
            },
          },
        },
        {
          name: 'CO2Offer',
          value: ' CO2-Rabatt: 10% bereits abgezogen',
        },
        {
          name: 'cover_details_classic',
          value: {
            info: {
              description: ' Der umfassenden Rundum-Schutz zum günstigen Preis',
              coverageDescription: 'Kfz-Haftpflicht',
            },
            features: [
              {
                name: 'headline_liability',
                list: [
                  {
                    name: 'Liability_includes_sign_1',
                    available: true,
                    label: 'Deckungssummen 100 Mio. pauschal, max. 15 Mio. pro Person',
                  },
                  {
                    name: 'Liability_includes_sign_2',
                    available: true,
                    label: 'Mallorca Police',
                  },
                  {
                    name: 'Liability_includes_sign_3',
                    available: false,
                    label: 'Eigenschadendeckung bei Sachschäden',
                  },
                ],
                label: 'Haftpflicht-Leistungen im Klassik-Tarif',
              },
              {
                name: 'headline_comprehensive',
                list: [
                  {
                    name: 'comprehensive_includes_sign_1',
                    available: true,
                    label: 'Schäden durch grobe Fahrlässigkeit',
                  },
                  {
                    name: 'comprehensive_includes_sign_2',
                    available: true,
                    label: 'Schäden durch Tiere aller Art',
                  },
                  {
                    name: 'comprehensive_includes_sign_3',
                    available: true,
                    label: 'Folgeschäden bei Tierbiss-Schäden bis 5.500 Euro',
                  },
                ],
                label: 'Teilkasko-Leistungen im Klassik-Tarif',
              },
              {
                name: 'headline_full_comprehensive',
                list: [
                  {
                    name: 'full_comprehensive_includes_sign_1',
                    available: false,
                    label: 'Leihwagen nach Unfall bzw. nach Fahrzeugentwendung',
                  },
                  {
                    name: 'comprehensive_includes_sign_2',
                    label: 'Die vollständige Leistungsübersicht finden Sie auf unseren Informationsseiten',
                  },
                ],
                label: 'Vollkasko-Leistungen im Klassik-Tarif (Teilkasko ist in Vollkasko enthalten)',
              },
            ],
            productLine: {
              value: 'Klassik',
              id: 'Classic',
            },
          },
        },
        {
          name: 'tariffOptionDescriptions',
          value: {
            KaskoClever: {
              overlayDescription:
                ' Mit der Kasko Clever überlassen Sie uns die Werkstattwahl und erhalten dafür einen Rabatt.',
              description: 'Mit cleveren Zusatzoptionen wie Werkstattbindung zahlen Sie weniger Prämie',
              features: [
                'Bequeme Unfallabwicklung',
                'Kostenlose Abholung des Unfallautos',
                'Garantiert repariert',
                'Kostenloses Ersatzfahrzeug',
              ],
              percentageText: 'auf die Kaskodeckung',
              title: 'Kasko Clever',
              percentage: 'bis zu 15',
            },
            SCH: {
              title: 'Schutzbrief',
              overlayDescription:
                'Unser Schutzbrief sorgt dafür, dass Sie auch im Fall einer Panne immer Hilfe erhalten und das in ganz Europa.',
              description: 'Bei einer Panne sind wir für Sie da.',
            },
            FAS: {
              title: 'Fahrerschutz',
              overlayDescription:
                'TEST Mit dem Fahrerschutz sichert sich der Fahrer ab, falls doch mal etwas passiert. Wir zahlen pro Versicherungsfall bis zu 15 Millionen für beispielsweise Verdienstausfall, Haushaltshilfe, Hinterbliebenenrente usw..',
              description: 'So sind Sie auch als Fahrer geschützt.',
            },
            NPT: {
              title: 'Nix-Passiert-Tarif',
              overlayDescription:
                'Mit dem Nix-Passiert-Tarif können Sie Ihre Schadenfreiheitsklasse schützen, solange es bei einem Schaden pro Jahr bleibt.',
              description: 'Schützt Ihren Schadenfreiheitsrabatt.',
            },
            VRS: {
              title: 'Verkehrsrechtsschutz',
              overlayDescription:
                'Mit unserem Verkehrsrechtsschutz sind Sie vor den finanziellen Folgen geschützt. Egal ob im Auto, auf dem Rad oder zu Fuß. Und das ganz ohne Wartezeiten.',
              description: 'Wir schützen Sie auch vor finanziellen Folgen.',
            },
            FKK: {
              title: 'Familienschutz "Kind &Kegel"',
              overlayDescription:
                'Schützen Sie sich und alle anderen Mitfahrer. Zusatzleistungen bei einem Krankenhausaufenthalt kommen besonders Kindern zugute. Auch Tierarztkosten für verunfallte Haustiere sind bis 1.000 Euro eingeschlossen. ',
              description: 'Schützen Sie sich und Ihre Mitfahrer.',
            },
          },
        },
        {
          name: 'cover_details_premium',
          value: {
            info: {
              description: ' Optimale Absicherung mit Premium-Schutz',
              coverageDescription: 'Kfz-Haftpflicht',
            },
            features: [
              {
                name: 'headline_liability',
                list: [
                  {
                    name: 'Liability_includes_sign_1',
                    available: true,
                    label: 'Deckungssummen 100 Mio. pauschal, max. 15 Mio. pro Person',
                  },
                  {
                    name: 'Liability_includes_sign_2',
                    available: true,
                    label: 'Mallorca Police',
                  },
                  {
                    name: 'Liability_includes_sign_3',
                    available: true,
                    label: 'Eigenschadendeckung bei Sachschäden',
                  },
                ],
                label: 'Haftpflicht-Leistungen im Premium-Tarif',
              },
              {
                name: 'headline_comprehensive',
                list: [
                  {
                    name: 'comprehensive_includes_sign_1',
                    available: true,
                    label: 'Schäden durch grobe Fahrlässigkeit',
                  },
                  {
                    name: 'comprehensive_includes_sign_2',
                    available: true,
                    label: 'Schäden durch Tiere aller Art',
                  },
                  {
                    name: 'comprehensive_includes_sign_3',
                    available: true,
                    label: 'Folgeschäden bei Tierbiss-Schäden bis 5.500 Euro',
                  },
                ],
                label: 'Teilkasko-Leistungen im Premium-Tarif',
              },
              {
                name: 'headline_full_comprehensive',
                list: [
                  {
                    name: 'full_comprehensive_includes_sign_1',
                    available: true,
                    label: 'Leihwagen nach Unfall bzw. nach Fahrzeugentwendung',
                  },
                  {
                    name: 'comprehensive_includes_sign_2',
                    label: 'Die vollständige Leistungsübersicht finden Sie auf unseren Informationsseiten',
                  },
                ],
                label: 'Vollkasko-Leistungen im Premium-Tarif (Teilkasko ist in Vollkasko enthalten)',
              },
            ],
            productLine: {
              value: 'Premium',
              id: 'Premium',
            },
          },
        },
        {
          name: 'cover_details_basis',
          value: {
            info: {
              description: ' Alle wichtigen Absicherungen in der Basis-Absicherung.',
              coverageDescription: 'Kfz-Haftpflicht',
            },
            features: [
              {
                name: 'headline_liability',
                list: [
                  {
                    name: 'Liability_includes_sign_1',
                    available: true,
                    label: 'Deckungssummen 100 Mio. pauschal, max. 15 Mio. pro Person',
                  },
                  {
                    name: 'Liability_includes_sign_2',
                    available: false,
                    label: 'Mallorca Police',
                  },
                  {
                    name: 'Liability_includes_sign_3',
                    available: false,
                    label: 'Eigenschadendeckung bei Sachschäden',
                  },
                ],
                label: 'Haftpflicht-Leistungen im Basis-Tarif',
              },
              {
                name: 'headline_comprehensive',
                list: [
                  {
                    name: 'comprehensive_includes_sign_1',
                    available: false,
                    label: 'Schäden durch grobe Fahrlässigkeit',
                  },
                  {
                    name: 'comprehensive_includes_sign_2',
                    available: true,
                    label: 'Schäden durch Tiere aller Art',
                  },
                  {
                    name: 'comprehensive_includes_sign_3',
                    available: false,
                    label: 'Folgeschäden bei Tierbiss-Schäden bis 5.500 Euro',
                  },
                ],
                label: 'Teilkasko-Leistungen im Basis-Tarif',
              },
              {
                name: 'headline_full_comprehensive',
                list: [
                  {
                    name: 'full_comprehensive_includes_sign_1',
                    available: false,
                    label: 'Leihwagen nach Unfall bzw. nach Fahrzeugentwendung',
                  },
                  {
                    name: 'comprehensive_includes_sign_2',
                    label: 'Die vollständige Leistungsübersicht finden Sie auf unseren Informationsseiten',
                  },
                ],
                label: 'Vollkasko-Leistungen im Basis-Tarif (Teilkasko ist in Vollkasko enthalten)',
              },
            ],
            productLine: {
              value: 'Basis',
              id: 'Basic',
            },
          },
        },
        {
          name: 'thank_you_mgm',
          value: {
            Graphic2: [
              'Freunden von Verti erzählen',
              'Einen Freund als Neukunden zu Verti bringen',
              'Rabatt von 50 € sichern',
            ],
            Graphic1: {
              price: {
                amount: '50',
                currency: '€',
                text: 'Gutschrift erhalten',
                separator: ',',
                decimals: '00',
              },
            },
          },
        },
        {
          name: 'YEBVerti',
          value: {
            YEBPreSeasonStart: '24/08/2023',
            YEBPostSeasonEnd: '14/02/2024',
            YEBSeasonStart: '24/09/2023',
            YEBSeasonEnd: '21/01/2024',
          },
        },
      ],
      logicModules: [
        {
          logicModuleCode: 'pricing',
          image: {
            name: 'pricing.png',
            url: 'https://d1nqe8q64bfnzv.cloudfront.net/logic-module-data/images/3f8c1d9e-7b2a-4d6b-9f8c-1e7b2a4d6b9f/pricing.png',
          },
          logicModuleId: '3f8c1d9e-7b2a-4d6b-9f8c-1e7b2a4d6b9f',
          activated: false,
        },
        {
          logicModuleCode: 'informative',
          image: {
            name: 'mar.png',
            url: 'https://d1nqe8q64bfnzv.cloudfront.net/logic-module-data/images/a1b2c3d4-e5f6-7a8b-9c0d-e1f2a3b4c5d6/mar.png',
          },
          logicModuleId: 'a1b2c3d4-e5f6-7a8b-9c0d-e1f2a3b4c5d6',
          activated: true,
        },
      ],
    });
  }

  static postQuoting(): Observable<Quoting> {
    return of({
      evbNumber: 'TCR903851982',
      referenceNumberCarPolicy: 'REF12345677',
      referenceNumberLegalPolicy: '',
      speechBubbleText: '',
      carPolicyFiles: [],
      legalPolicyFiles: [],
    });
  }

  /**
   * Vehicle Search Services
   */
  static postManufacturer(): Observable<ManufacturerResponse> {
    return of({
      manufacturers: [
        'AUDI',
        'BMW',
        'FORD',
        'MERCEDES-BENZ',
        'OPEL',
        'RENAULT',
        'VW',
        'AIWAYS',
        'ALFA ROMEO',
        'ALPINA',
        'ALPINE',
        'BYD',
        'CADILLAC',
        'CHEVROLET',
        'CHRYSLER',
        'CITROEN',
        'CORVETTE',
        'CUPRA',
        'DACIA',
        'DAEWOO',
        'DAIHATSU',
        'DODGE',
        'E.GO',
        'FIAT',
        'GENESIS',
        'GWM',
        'HONDA',
        'HYUNDAI',
        'INEOS',
        'INFINITI',
        'ISUZU',
        'JAGUAR',
        'JEEP',
        'KIA',
        'LADA',
        'LAMBORGHINI',
        'LANCIA',
        'LAND ROVER',
        'LANDWIND',
        'LEXUS',
        'LOTUS',
        'LYNK & CO',
        'MASERATI',
        'MAZDA',
        'MG',
        'MINI',
        'MITSUBISHI',
        'NIO',
        'NISSAN',
        'ORA',
        'PEUGEOT',
        'POLESTAR',
        'PORSCHE',
        'RAM TRUCKS',
        'ROVER',
        'SAAB',
        'SEAT',
        'SKODA',
        'SMART',
        'SSANGYONG',
        'SUBARU',
        'SUZUKI',
        'TATA',
        'TESLA',
        'TOYOTA',
        'VINFAST',
        'VOLVO',
        'WEY',
        'XPENG',
        'ZEEKR',
      ],
    });
  }

  static postModelSeries(): Observable<ModelSeriesResponse> {
    return of({
      modelSeries: [
        'A1',
        'A3',
        'A4',
        'A4 Allroad Quattro',
        'A5',
        'A6',
        'A6 Allroad Quattro',
        'A6 E-Tron',
        'A7',
        'A8',
        'Audi TT',
        'E-Tron',
        'E-Tron GT',
        'Q2',
        'Q3',
        'Q3 Sportback',
        'Q4 E-Tron',
        'Q5',
        'Q5 Sportback',
        'Q6 E-Tron',
        'Q7',
        'Q8',
        'Q8 E- Tron Sportback',
        'Q8 E-Tron',
        'R8',
      ],
    });
  }

  static postEngineType(): Observable<EngineTypeResponse> {
    return of({
      engineTypes: ['Petrol', 'Diesel', 'Gas', 'PetrolElectricPlugIn'],
    });
  }

  static postCategory(): Observable<CategoryResponse> {
    return of({
      categories: ['Wagon', 'Limousine'],
    });
  }

  static postPower(): Observable<PowerResponse> {
    return of({
      powers: [
        {
          ps: 116,
          kw: 85,
        },
        {
          ps: 150,
          kw: 110,
        },
        {
          ps: 200,
          kw: 147,
        },
      ],
    });
  }

  static postVehicles(): Observable<VehiclesResponse> {
    return of({
      vehicles: [
        {
          hsn: '0588',
          tsn: 'BUN',
          manufacturer: 'AUDI',
          modelDescription: 'A3 35 TDI Limousine',
          engineType: 'Diesel',
          power: {
            ps: 150,
            kw: 110,
          },
          productionStart: 2020,
          engineCapacity: 1968,
          khClass: 19,
          tkClass: 23,
          vkClass: 23,
        },
        {
          hsn: '0588',
          tsn: 'BZO',
          manufacturer: 'AUDI',
          modelDescription: 'A3 35 TDI Limousine',
          engineType: 'Diesel',
          power: {
            ps: 150,
            kw: 110,
          },
          productionStart: 2020,
          engineCapacity: 1968,
          khClass: 19,
          tkClass: 23,
          vkClass: 23,
        },
        {
          hsn: '0588',
          tsn: 'BUN',
          manufacturer: 'AUDI',
          modelDescription: 'A3 35 TDI Limousine S tronic',
          engineType: 'Diesel',
          power: {
            ps: 150,
            kw: 110,
          },
          productionStart: 2020,
          engineCapacity: 1968,
          khClass: 19,
          tkClass: 23,
          vkClass: 23,
        },
        {
          hsn: '0588',
          tsn: 'BZO',
          manufacturer: 'AUDI',
          modelDescription: 'A3 35 TDI Limousine S tronic',
          engineType: 'Diesel',
          power: {
            ps: 150,
            kw: 110,
          },
          productionStart: 2020,
          engineCapacity: 1968,
          khClass: 19,
          tkClass: 23,
          vkClass: 23,
        },
      ],
    });
  }

  static getVehicle(): Observable<VehicleResponse> {
    return of({
      vehicle: {
        hsn: '0588',
        tsn: 'BUN',
        manufacturer: 'AUDI',
        modelSeries: 'A3',
        modelDescription: 'A3 35 TDI Limousine',
        engineType: 'Diesel',
        power: {
          ps: 150,
          kw: 110,
        },
        category: 'Limousine',
        productionStart: 2020,
        engineCapacity: 1968,
        khClass: 19,
        tkClass: 23,
        vkClass: 23,
        co2Rebate: false,
      },
    });
  }

  /**
   * Validation Services
   */
  static postPostCode(): Observable<PostCodeResponse> {
    return of({
      isValid: true,
    });
  }

  static postEmail(): Observable<EmailResponse> {
    return of({
      isValid: true,
    });
  }

  static postPromotionCode(): Observable<PromotionCodeResponse> {
    return of({
      isValid: true,
    });
  }

  static postIban(): Observable<IbanResponse> {
    return of({
      isValid: true,
    });
  }

  static getSummaryLiteData(): SummaryLiteData {
    return {
      title: 'PlusBonus',
      subtitle: 'BMW 330d Coupe',
      priceDetail: {
        price: '12126,00',
        frequency: 'Jahrlich',
        currency: '€',
      },
      coveragesSections: [
        {
          title: 'Sonderangebote',
          coverages: [
            {
              name: 'KASKO CLEVER PROMO (-15%)',
              value: '-31,95 €',
            },
          ],
        },
        {
          title: 'Extra-Abdeckung',
          coverages: [
            {
              name: 'Schutzbrief',
              value: '23,21 €',
            },
          ],
        },
      ],
      deducible: 'Vollkasko 500€ / Teilkasko 150€ ',
      summarySections: [
        {
          id: 'POLICYHOLDER',
          title: 'Policyholder',
          isExpanded: true,
          subSections: [
            {
              id: 'POLICYHOLDER',
              title: 'Policyholder',
              name: 'Sergio Calahorra',
              address: 'Maurice Ravel',
            },
            {
              id: 'FAMILY',
              title: 'Partner',
              name: 'Julia Rodriguez',
            },
          ],
        },
        {
          id: 'VEHICLE_AND_PREV_INSURANCE',
          title: 'Vehicle',
          isExpanded: false,
          subSections: [
            {
              id: 'VEHICLE_OWNER',
              title: 'Vehicle Owner',
              name: 'Perico Flores',
              address: 'Test Test',
            },
          ],
        },
      ],
    };
  }
}
